import { FC, useCallback } from 'react';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import ExportDropdown from 'components/Shared/ExportDropdown';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { useExportEventsMutation } from 'redux/apiSlice/events.slice';
import { buildEventsExportBody } from 'data/convertors/events.convertors';
import { TRtkErrorType } from 'data/types/generic.types';
import { TEventExportProps } from './EventExport.types';

const EventExport: FC<TEventExportProps> = ({
  selectedRowKeys,
  selectedFilters,
  sortColumn,
  myEvents,
  columnKeys,
  disabled,
}) => {
  const [exportEvents, { isLoading: isExporting }] = useExportEventsMutation();
  const { showToastNotification } = useShowToastNotification();

  // Visible columns in the order shown on screen, from the table settings.
  const getVisibleColumnKeys = useCallback((): string[] => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[ETableStoragePaths.EVENTS] as
      | TColumnsState[]
      | undefined;
    if (columnsState?.length) {
      return columnsState.filter(col => col.show).map(col => String(col.key));
    }
    return columnKeys;
  }, [columnKeys]);

  const runExport = async (eventIds?: string[]) => {
    try {
      await exportEvents(
        buildEventsExportBody({
          filters: selectedFilters,
          sortColumn,
          visibleColumnKeys: getVisibleColumnKeys(),
          myEvents,
          eventIds,
        }),
      ).unwrap();
    } catch (err) {
      showToastNotification({
        message:
          (err as TRtkErrorType)?.data?.message ||
          'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleExportSelected = () => {
    if (!selectedRowKeys.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    runExport(selectedRowKeys.map(key => String(key)));
  };

  return (
    <ExportDropdown
      onExportAll={() => runExport()}
      onExportSelected={handleExportSelected}
      isExporting={isExporting}
      disabled={disabled}
    />
  );
};

export default EventExport;
